import { useEffect, useRef } from 'react'
import { markLocalDiagramChange } from '@/services/realtime/touched'
import { useDiagramStore } from '@/stores/diagramStore'
import { useDraftStore } from '@/stores/draftStore'

const DEBOUNCE_MS = 350
const RESCHEDULE_WHILE_SAVING_MS = 250

/**
 * Subscribes to diagram changes and schedules a remote draft save.
 * Uses a short debounce so collaborative edits travel quickly without saving
 * every intermediate drag frame.
 *
 * Mount this hook once at the editor level (e.g. inside AppShell).
 */
export function useAutosave(): void {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const scheduleSave = (delay = DEBOUNCE_MS) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(async () => {
        debounceTimerRef.current = null
        const store = useDraftStore.getState()
        if (!store.currentProjectId || !store.isDirty) return
        if (store.saveStatus === 'saving') {
          scheduleSave(RESCHEDULE_WHILE_SAVING_MS)
          return
        }
        await store.performSave().catch(() => {/* SaveStatus shows the error */})
        const afterSave = useDraftStore.getState()
        if (afterSave.isDirty && afterSave.saveStatus !== 'saving') {
          scheduleSave(RESCHEDULE_WHILE_SAVING_MS)
        }
      }, delay)
    }

    const unsubscribe = useDiagramStore.subscribe((state, prev) => {
      if (
        state.tables === prev.tables &&
        state.rels === prev.rels &&
        state.groups === prev.groups &&
        state.defaults === prev.defaults &&
        state.projectName === prev.projectName &&
        state.dialect === prev.dialect
      ) {
        return
      }

      if (useDraftStore.getState().autosaveSuppressed) return

      markLocalDiagramChange(prev, state)
      useDraftStore.getState().markDirty()

      const { currentProjectId } = useDraftStore.getState()
      if (!currentProjectId) return
      scheduleSave()
    })

    return () => {
      unsubscribe()
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])
}
